import { Form, FormInstance, Input, Typography, Button, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import PredefinedReasonsDropdown from '../../../components/PredefinedReasonsDropdown';
import Icon from '../../../components/ui-components/Icon';
import RedDivideCircle from '../../../assets/images/red-divide-circle.svg';
import DivideCircle from '../../../assets/images/divide-circle.svg';
import GreenTick from '../../../assets/images/green-tick.svg';
import GrayTick from '../../../assets/images/gray-tick.svg';
import BlueTick from '../../../assets/images/blue-tick.svg';
import {
	PreworkFeedback,
	PreworkReviewAction,
} from '../../../shared/PreWorkServiceTypes';

const { Text } = Typography;
const { useBreakpoint } = Grid;
export interface PreWorkRecommendationUIConfig {
	commentsMandatory?: boolean;
	commentsLabel?: string;
	commentsPlaceholder?: string;
	form?: FormInstance;
	category?: string;
	displayComments?: boolean;
	disableReview?: boolean;
	hideReasons?: boolean;
	disabled?: boolean;
}

export const PreWorkRecommendation = ({
	commentsMandatory,
	commentsLabel = 'comments',
	commentsPlaceholder,
	form,
	category,
	displayComments,
	disableReview = false,
	disabled,
}: PreWorkRecommendationUIConfig) => {
	const { t, i18n } = useTranslation();
	const [commentsRequired, setCommentsRequired] = useState<boolean>(
		commentsMandatory ?? false
	);
	const [lengthyLanguage, setLengthyLanguage] = useState<boolean>();

	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = lengthyLanguage && isMobile;
	const [reasonDropdownVisible, setReasonDropdownVisible] =
		useState<boolean>(false);
	const values = Form.useWatch<PreworkFeedback>([], form);
	const [selectedReview, setSelectedReview] = useState<string | null>(null);
	const reviewValue = Form.useWatch('preworkRecommendation', form) as string;

	useEffect(() => {
		setSelectedReview(reviewValue || null);
		setReasonDropdownVisible(
			reviewValue === PreworkReviewAction.PREWORK_REJECT
		);
	}, [reviewValue]);

	useEffect(() => {
		const lengthyLanguages = ['en', 'ta', 'ma', 'ka', 'te', 'hi'];

		setLengthyLanguage(lengthyLanguages.includes(i18n.language));
	}, [i18n.language]);

	useEffect(() => {
		const isDisapproved =
			values?.preworkRecommendation === PreworkReviewAction.PREWORK_REJECT;

		if (commentsMandatory) {
			setCommentsRequired(true);
		} else {
			setCommentsRequired(isDisapproved);
		}
	}, [commentsMandatory, values, category]);

	const handleReviewChange = useCallback(
		(value: string) => {
			setSelectedReview(value);
			if (form) {
				form.setFieldsValue({ preworkRecommendation: value });
				if (
					value === PreworkReviewAction.PREWORK_APPROVE ||
					value === PreworkReviewAction.EXTEND_TO_PREWORK_MENTOR
				) {
					form.setFieldsValue({ predefinedReasons: undefined });
					setReasonDropdownVisible(false);
				} else if (value === PreworkReviewAction.PREWORK_REJECT) {
					setReasonDropdownVisible(true);
				}
			}

			if (commentsMandatory) {
				setCommentsRequired(true);
				return;
			}

			if (value && value !== PreworkReviewAction.PREWORK_APPROVE) {
				setCommentsRequired(true);
				return;
			}

			setCommentsRequired(false);
		},
		[commentsMandatory, form]
	);

	const buttonProps =
		'w-44 h-9 font-sans font-bold text-[13px] text-gray-650 hover:!text-gray-650';

	return (
		<div className="flex flex-row items-start sm:mt-0 md:mt-4">
			<div className="flex flex-col w-full">
				{displayComments && (
					<>
						<Text className="flex flex-wrap text-[13px] md:text-[14px]">
							<span>
								{t(commentsLabel)}
								{commentsRequired && (
									<span className="text-red-500">
										&nbsp;<b>*</b>
									</span>
								)}
							</span>
						</Text>
						<Form.Item
							className="font-sans font-bold"
							name={['comments']}
							rules={[
								{
									required: commentsRequired,
									message: <span>{t('required')}</span>,
								},
								{
									min: 10,
									message: <span>{t('commentsShouldBeClear')}</span>,
								},
							]}
						>
							<Input.TextArea
								showCount
								rows={4}
								placeholder={
									commentsRequired
										? commentsPlaceholder
											? `${t(commentsPlaceholder)}`
											: `${t('commentsAreMandatoryNGF')}`
										: ''
								}
								className="font-sans text-sm font-normal"
								disabled={disabled}
							/>
						</Form.Item>
					</>
				)}
				<Form.Item
					name={['preworkRecommendation']}
					rules={[
						{
							required: true,
							message: <span>{t('answerIsRequired')}</span>,
						},
					]}
				>
					<div className="flex justify-center mb-4">
						<Button
							icon={
								<Icon
									src={
										selectedReview ===
										PreworkReviewAction.EXTEND_TO_PREWORK_MENTOR
											? BlueTick
											: GrayTick
									}
									width={'w-5'}
									height={'h-5'}
								/>
							}
							onClick={() =>
								handleReviewChange(PreworkReviewAction.EXTEND_TO_PREWORK_MENTOR)
							}
							iconPosition="end"
							className={`
                  w-fit whitespace-normal border-solid border border-approve-border-blue
                  ${buttonProps} 
                  hover:!text-gray-650 hover:!bg-approve-blue 
                  ${
										selectedReview ===
										PreworkReviewAction.EXTEND_TO_PREWORK_MENTOR
											? '!bg-approve-blue'
											: 'bg-transparent'
									} 
                  ${
										disableReview || disabled
											? 'cursor-not-allowed'
											: 'cursor-pointer'
									}
                  ${
										(disableReview || disabled) &&
										selectedReview !==
											PreworkReviewAction.EXTEND_TO_PREWORK_MENTOR
											? 'opacity-50'
											: 'opacity-100'
									}
                  w-fit
                `}
							disabled={disableReview || disabled}
						>
							{t('extendPrework')}
						</Button>
					</div>

					<div
						className={`${
							isStackVertical
								? 'flex flex-col items-center'
								: 'flex justify-center'
						} gap-4`}
					>
						<Button
							icon={
								<Icon
									src={
										selectedReview === PreworkReviewAction.PREWORK_REJECT
											? RedDivideCircle
											: DivideCircle
									}
									width={'w-5'}
									height={'h-5'}
								/>
							}
							disabled={disableReview || disabled}
							onClick={() =>
								handleReviewChange(PreworkReviewAction.PREWORK_REJECT)
							}
							iconPosition="end"
							className={`
                ${buttonProps} 
                border border-solid !border-ngf-border-orange
                hover:!bg-ngf-orange 
                ${
									selectedReview === PreworkReviewAction.PREWORK_REJECT
										? '!bg-ngf-orange'
										: 'bg-transparent'
								} 
                ${
									(disableReview || disabled) &&
									selectedReview !== PreworkReviewAction.PREWORK_REJECT
										? 'opacity-50'
										: 'opacity-100'
								}
                ${
									disableReview || disabled
										? 'cursor-not-allowed'
										: 'cursor-pointer'
								}
                w-fit
              `}
						>
							{t('notGoingForward')}
						</Button>
						<Button
							icon={
								<Icon
									src={
										selectedReview === PreworkReviewAction.PREWORK_APPROVE
											? GreenTick
											: GrayTick
									}
									width={'w-5'}
									height={'h-5'}
								/>
							}
							onClick={() =>
								handleReviewChange(PreworkReviewAction.PREWORK_APPROVE)
							}
							iconPosition="end"
							className={`border border-solid !border-approve-border-green
                ${buttonProps} 
                hover:!text-gray-650 hover:!bg-approve-green 
                ${
									selectedReview === PreworkReviewAction.PREWORK_APPROVE
										? '!bg-approve-green'
										: 'bg-transparent'
								}
                ${
									(disableReview || disabled) &&
									selectedReview !== PreworkReviewAction.PREWORK_APPROVE
										? 'opacity-50'
										: 'opacity-100'
								}
                ${
									disableReview || disabled
										? 'cursor-not-allowed'
										: 'cursor-pointer'
								}
              `}
							disabled={disableReview || disabled}
						>
							{t('approve')}
						</Button>
					</div>
				</Form.Item>
				{reasonDropdownVisible && (
					<>
						<Text>
							{t('reason')}{' '}
							{
								<span className="text-red-500">
									<b>*</b>
								</span>
							}
						</Text>
						<Form.Item
							name={['predefinedReasons']}
							rules={[
								{
									required: true,
									message: <span>{t('required')}</span>,
								},
							]}
						>
							<PredefinedReasonsDropdown
								category={category}
								disabled={disableReview || disabled}
							/>
						</Form.Item>
					</>
				)}
			</div>
		</div>
	);
};
